import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PdfSettings } from './pdf-settings';
import { Content, TableCell, Table, Column } from 'pdfmake/interfaces';
import { ShoppingCart } from '@pages/common-order/_interfaces_/shopping-cart.interface';
import { OrderStatusEnum } from '@enums/order-status.enum';

@Injectable({
  providedIn: 'root',
})
export class ShoppingCartPdfService {
  constructor(private translateService: TranslateService) {}

  public getContent(status: string, shoppingCart: ShoppingCart[]): Content {
    if (status !== OrderStatusEnum.BOOKED) {
      const content: Content = [];
      shoppingCart.forEach((cart) => {
        content.push(this.getShoppingCartContent(cart));
      });
      return content;
    }

    return null;
  }

  private getShoppingCartContent(shoppingCart: ShoppingCart): Content {
    const priceOverviewHeader = { text: this.translateService.instant('PRINT.PRICE_OVERVIEW'), style: 'subheader' };
    const ticketPricesSectionHeader = { text: this.translateService.instant('PRINT.TICKET_PRICES'), style: 'sectionheader' };
    const ticketPriceTable = this.getBasicPriceTable(shoppingCart.priceOverview);
    const totalPriceColumns = this.getTotalPriceColumns(shoppingCart.totalCost);
    const content: Content = [
      { text: priceOverviewHeader },
      { text: ticketPricesSectionHeader },
      { table: ticketPriceTable, layout: 'lightHorizontalLines' },
      { text: '\n' },
      { columns: totalPriceColumns },
      { text: '\n\n\n' },
    ];

    if (
      shoppingCart.otherCosts &&
      Object.keys(shoppingCart.otherCosts).length > 0 &&
      shoppingCart.otherCosts.bookingCost &&
      shoppingCart.otherCosts.bookingCost?.length > 0
    ) {
      const otherCostsSectionHeader = { text: this.translateService.instant('PRINT.OTHER_COSTS'), style: 'sectionheader' };
      const otherPriceTable = this.getOtherPriceTable(shoppingCart);
      return [
        ...content.slice(0, 4),
        { text: otherCostsSectionHeader },
        { table: otherPriceTable, layout: 'lightHorizontalLines' },
        { text: '\n' },
        ...content.slice(4),
      ];
    }

    return content;
  }

  private getBasicPriceTable(priceOverview): Table {
    const body: TableCell[][] = [
      [
        this.translateService.instant('PRINT.PASSANGER_TYPE'),
        this.translateService.instant('PRINT.TICKET'),
        this.translateService.instant('PRINT.TAXES'),
        this.translateService.instant('PRINT.FEE'),
        this.translateService.instant('PRINT.DISCONT'),
        this.translateService.instant('PRINT.COUNT'),
        this.translateService.instant('PRINT.TOTAL'),
      ],
    ];

    priceOverview.forEach((pax) => body.push(this.getPaxPriceTableRow(pax)));

    return {
      body,
      widths: [75, '*', '*', '*', '*', '*', '*'],
    };
  }

  private getPaxPriceTableRow(pax): TableCell[] {
    return [
      { text: pax.passengerType ? this.translateService.instant(pax.passengerType) : '-' },
      { text: pax.ticketPrice ? `€${pax.ticketPrice}` : '-', alignment: 'right', margin: PdfSettings.styles.tableCell.margin },
      {
        text: pax.totalTaxPerPassenger ? `€${pax.totalTaxPerPassenger}` : '-',
        alignment: 'right',
        margin: PdfSettings.styles.tableCell.margin,
      },
      { text: pax.ticketFee ? `€${pax.ticketFee}` : '-', alignment: 'right', margin: PdfSettings.styles.tableCell.margin },
      {
        text: pax.discountPerPassenger ? `- €${pax.discountPerPassenger}` : '-',
        alignment: 'right',
        margin: PdfSettings.styles.tableCell.margin,
      },
      { text: pax.numberOfPassengers ? `x ${pax.numberOfPassengers}` : '' },
      { text: pax.totalPrice ? `€${pax.totalPrice}` : '-', alignment: 'right' },
    ];
  }

  private getOtherPriceTable(shoppingCart): Table {
    const body: TableCell[][] = [
      [
        this.translateService.instant('PRINT.OTHER_TYPE'),
        { text: this.translateService.instant('PRINT.OTHER_PRICE'), alignment: 'center' },
        this.translateService.instant('PRINT.OTHER_COUNT'),
        this.translateService.instant('PRINT.OTHER_TOTAL'),
      ],
    ];

    if (shoppingCart.otherCosts?.ticketInsurance) {
      body.push(this.getTicketInsuranceRow(shoppingCart.otherCosts.ticketInsurance));
    }

    if (shoppingCart.otherCosts?.bookingCost) {
      body.push(this.getBookingCostsRow(shoppingCart.otherCosts.bookingCost));
    }

    if (shoppingCart.additionalProducts?.ancillaryServices?.length) {
      body.push(...this.getAdditionalProductsRow(shoppingCart.additionalProducts.ancillaryServices));
    }

    return { body, widths: [200, 110, 105, '*'] };
  }

  private getTicketInsuranceRow(ticketInsurance): TableCell[] {
    return [
      { text: this.translateService.instant('PRINT.INSURANCE_TYPE') },
      { text: `€${ticketInsurance.totalPerItem}`, alignment: 'right', margin: PdfSettings.styles.tableCell.margin },
      { text: `x ${ticketInsurance.numberOfItems}` },
      { text: `€${ticketInsurance.totalPerItem}`, alignment: 'right' },
    ];
  }

  private getBookingCostsRow(bookingCost): TableCell[] {
    const bookingCostsRow = [
      { text: this.translateService.instant('PRINT.RESERVATION_COSTS') },
      { text: `€${bookingCost}`, alignment: 'right', margin: PdfSettings.styles.tableCell.margin },
      { text: 'x 1' },
      { text: `€${bookingCost}`, alignment: 'right' },
    ];
    return bookingCostsRow;
  }

  private getAdditionalProductsRow(additionalProducts): TableCell[][] {
    const cells: TableCell[][] = [];
    additionalProducts.forEach((p) => {
      const totalPrice = p.totalPrice && p.totalPrice.length > 0;
      const priceMultipliedByNumberOfPieces = p.priceMultipliedByNumberOfPieces && p.priceMultipliedByNumberOfPieces.length > 0;
      cells.push([
        { text: p.productName || '-' },
        totalPrice
          ? { text: `€${p.totalPrice}`, alignment: 'right', margin: PdfSettings.styles.tableCell.margin }
          : { text: '-', alignment: 'right' },
        { text: p.numberOfPieces ? `x ${p.numberOfPieces}` : '-' },
        priceMultipliedByNumberOfPieces
          ? { text: `€${p.priceMultipliedByNumberOfPieces}`, alignment: 'right' }
          : { text: '-', alignment: 'right' },
      ]);
    });
    return cells;
  }

  private getTotalPriceColumns(totalPrice): Column[] {
    return [
      { text: this.translateService.instant('PRINT.TOTAL_ORDER_COSTS'), alignment: 'left', style: 'header' },
      { text: `€${totalPrice}`, alignment: 'right', style: 'header' },
    ];
  }
}
